<template>
  <DataCard
    title="Choisir ou créer une gamme de tailles"
    icon="fas fa-plus-circle"
    icon-color="text-green-600"
  >
    <form @submit.prevent="handleSubmit" class="space-y-6">
      <!-- Mode de création -->
      <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h4 class="text-lg font-semibold text-blue-900 mb-4">Mode de création</h4>
        <div class="flex space-x-4">
          <label class="flex items-center">
            <input 
              type="radio" 
              v-model="creationMode" 
              value="custom"
              class="mr-2"
            />
            <span class="text-sm font-medium text-gray-700">Créer ma propre déclinaison</span>
          </label>
          <label class="flex items-center">
            <input 
              type="radio" 
              v-model="creationMode" 
              value="template"
              class="mr-2"
            />
            <span class="text-sm font-medium text-gray-700">Utiliser une déclinaison existante</span>
          </label>
        </div>
      </div>

      <!-- Section Déclinaison Existante -->
      <div v-if="creationMode === 'template'" class="bg-gray-50 p-4 rounded-lg border">
        <h4 class="text-lg font-semibold text-gray-900 mb-4">Déclinaison Existante</h4>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Nombre de déclinaisons -->
          <div>
            <label for="nb-declinaisons" class="block text-sm font-medium text-gray-700 mb-2">
              Combien avez vous de déclinaison ?
            </label>
            <div class="relative">
              <select 
                id="nb-declinaisons"
                v-model="selectedNbDeclinaisons"
                @change="filterDeclinaisons"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
                <option value="all">Affichez tout</option>
              </select>
            </div>
          </div>

          <!-- Liste des déclinaisons -->
          <div>
            <label for="liste-declinaisons" class="block text-sm font-medium text-gray-700 mb-2">
              Liste des déclinaisons
            </label>
            <div class="relative">
              <select 
                id="liste-declinaisons"
                v-model="selectedTemplate"
                @change="loadTemplate"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">-</option>
                <option 
                  v-for="declinaison in filteredDeclinaisons" 
                  :key="declinaison.id_declinaison"
                  :value="declinaison.id_declinaison"
                >
                  {{ declinaison.titre || 'Sans titre' }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Section Création Personnalisée -->
      <div v-if="creationMode === 'custom'" class="bg-gray-50 p-4 rounded-lg border">
        <h4 class="text-lg font-semibold text-gray-900 mb-4">Création Personnalisée</h4>
        
        <!-- Titre de la gamme -->
        <div class="mb-6">
          <label for="titre" class="block text-sm font-medium text-gray-700 mb-2">
            Nom de la gamme *
          </label>
          <input 
            id="titre"
            v-model="formData.titre"
            type="text"
            required
            placeholder="Ex: T-shirts Homme, Robes Femme, Chaussures..."
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :class="{ 'border-red-500': errors.titre }"
          />
          <p v-if="errors.titre" class="mt-1 text-sm text-red-600">
            {{ errors.titre }}
          </p>
        </div>

        <!-- Section des tailles -->
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <label class="block text-sm font-medium text-gray-700">
              Veuillez renseigner les tailles
            </label>
            <div class="text-sm text-gray-500">
              Colonnes d1 à d12
            </div>
          </div>
          
          <div class="grid grid-cols-6 gap-3">
            <div 
              v-for="(size, index) in sizeColumns" 
              :key="index"
              class="text-center"
            >
              <div 
                class="px-2 py-1 rounded-md text-sm font-medium border"
                :class="size ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'"
              >
                <input 
                  v-model="sizeColumns[index]"
                  type="text"
                  placeholder="-"
                  class="w-full text-center bg-transparent border-none outline-none text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end pt-4 border-t border-gray-200">
        <ActionButton
          label="Créer la gamme"
          icon="fas fa-plus"
          variant="primary"
          type="submit"
          :loading="isSubmitting"
          :disabled="!isFormValid"
        />
      </div>
    </form>
  </DataCard>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { DataCard, ActionButton } from '@/components'
import { useNotification } from '@/composables/useNotification'
import type { CreateGammeData, GammeComplete } from '@/types'

// Props
interface Props {
  allDeclinaisons: GammeComplete[]
}

const props = defineProps<Props>()

interface Emits {
  (e: 'create', data: CreateGammeData): void
}

const emit = defineEmits<Emits>()

// Notifications
const { error: showError } = useNotification()

const isSubmitting = ref(false)

// Mode de création
const creationMode = ref<'template' | 'custom'>('custom')

// Données pour les templates eFashion
const selectedNbDeclinaisons = ref('all')
const selectedTemplate = ref('')

// Déclinaisons filtrées
const filteredDeclinaisons = ref<GammeComplete[]>([])

const getDeclinaisonSizeCount = (declinaison: GammeComplete): number => {
  const sizes = [
    declinaison.d1_FR,
    declinaison.d2_FR,
    declinaison.d3_FR,
    declinaison.d4_FR,
    declinaison.d5_FR,
    declinaison.d6_FR,
    declinaison.d7_FR,
    declinaison.d8_FR,
    declinaison.d9_FR,
    declinaison.d10_FR,
    declinaison.d11_FR,
    declinaison.d12_FR,
  ]

  return sizes.filter((size) => size !== null && size !== undefined && size.trim() !== '').length
}

// Filtrer les déclinaisons selon le nombre de tailles sélectionné
const filterDeclinaisons = () => {
  selectedTemplate.value = '' // Réinitialiser la sélection
  
  // Filtrer les déclinaisons qui ont un titre valide
  const validDeclinaisons = props.allDeclinaisons.filter(d => d.titre && d.titre.trim() !== '')
  
  if (selectedNbDeclinaisons.value === 'all') {
    filteredDeclinaisons.value = validDeclinaisons
  } else {
    const targetCount = Number(selectedNbDeclinaisons.value)
    filteredDeclinaisons.value = validDeclinaisons.filter(
      (declinaison) => getDeclinaisonSizeCount(declinaison) === targetCount,
    )
  }
}

const formData = reactive<CreateGammeData>({
  titre: '',
  d1_FR: null,
  d2_FR: null,
  d3_FR: null,
  d4_FR: null,
  d5_FR: null,
  d6_FR: null,
  d7_FR: null,
  d8_FR: null,
  d9_FR: null,
  d10_FR: null,
  d11_FR: null,
  d12_FR: null,
})

// Colonnes de tailles pour la création personnalisée
const sizeColumns = ref<(string | null)[]>([
  null, null, null, null, null, null,
  null, null, null, null, null, null
])

const errors = reactive({
  titre: ''
})

// Vérifier si le formulaire est valide
const isFormValid = computed(() => {
  // Le titre doit être rempli
  if (!formData.titre || !formData.titre.trim()) {
    return false
  }
  
  // Au moins une colonne de taille doit être renseignée
  const hasAtLeastOneSize = sizeColumns.value.some(size => size && size.trim() !== '')
  
  return hasAtLeastOneSize
})

// Charger un template existant
const loadTemplate = () => {
  if (selectedTemplate.value) {
    const template = props.allDeclinaisons.find(d => d.id_declinaison.toString() === selectedTemplate.value)
    if (template) {
      formData.titre = template.titre || ''
      
      // Charger aussi les colonnes de tailles du template
      sizeColumns.value = [
        template.d1_FR,
        template.d2_FR,
        template.d3_FR,
        template.d4_FR,
        template.d5_FR,
        template.d6_FR,
        template.d7_FR,
        template.d8_FR,
        template.d9_FR,
        template.d10_FR,
        template.d11_FR,
        template.d12_FR,
      ]
    }
  } else {
    formData.titre = ''
  }
}

// Initialiser les déclinaisons filtrées
onMounted(() => {
  filterDeclinaisons()
})

// Watcher pour les props
watch(() => props.allDeclinaisons, () => {
  filterDeclinaisons()
}, { immediate: true })

// Synchroniser sizeColumns avec formData
watch(sizeColumns, (newColumns) => {
  formData.d1_FR = newColumns[0] || null
  formData.d2_FR = newColumns[1] || null
  formData.d3_FR = newColumns[2] || null
  formData.d4_FR = newColumns[3] || null
  formData.d5_FR = newColumns[4] || null
  formData.d6_FR = newColumns[5] || null
  formData.d7_FR = newColumns[6] || null
  formData.d8_FR = newColumns[7] || null
  formData.d9_FR = newColumns[8] || null
  formData.d10_FR = newColumns[9] || null
  formData.d11_FR = newColumns[10] || null
  formData.d12_FR = newColumns[11] || null
}, { deep: true })

// Réinitialiser le formulaire
const resetForm = () => {
  formData.titre = ''
  formData.d1_FR = null
  formData.d2_FR = null
  formData.d3_FR = null
  formData.d4_FR = null
  formData.d5_FR = null
  formData.d6_FR = null
  formData.d7_FR = null
  formData.d8_FR = null
  formData.d9_FR = null
  formData.d10_FR = null
  formData.d11_FR = null
  formData.d12_FR = null
  
  selectedTemplate.value = ''
  selectedNbDeclinaisons.value = 'all'
  
  // Réinitialiser les colonnes de tailles
  sizeColumns.value = [
    null, null, null, null, null, null,
    null, null, null, null, null, null
  ]
  
  errors.titre = ''
}

const validateForm = () => {
  errors.titre = ''
  
  if (!formData.titre.trim()) {
    errors.titre = 'Le nom de la gamme est requis'
    return false
  }
  
  // Vérifier qu'au moins une taille est renseignée
  const hasAtLeastOneSize = sizeColumns.value.some(size => size && size.trim() !== '')
  if (!hasAtLeastOneSize) {
    showError('Au moins une taille doit être renseignée')
    return false
  }
  
  return true
}

const handleSubmit = async () => {
  if (!validateForm()) return

  isSubmitting.value = true
  
  try {
    // formData est déjà synchronisé avec sizeColumns via le watcher
    emit('create', { ...formData })
    
    // Réinitialiser le formulaire
    resetForm()
  } catch (error) {
    showError('Erreur lors de la création de la gamme')
  } finally {
    isSubmitting.value = false
  }
}
</script>